import React from "react";
import { toast } from "react-toastify";

const MAX_DISPLAYED_ROWS = 5;

export type ReportRow = {
  row?: number;
  labelId: number | null;
  status: string;
  reason?: string | null;
};

export type ReportToastOptions = {
  title: string;
  counts: Record<string, number>;
  rows: ReportRow[];
  // status of the rows that went fine, they are not listed in the toast
  successStatus: string;
  fileName: string;
};

const CSV_COLUMNS: (keyof ReportRow)[] = ["row", "labelId", "status", "reason"];

const escapeCsv = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const downloadReport = (rows: ReportRow[], fileName: string) => {
  const columns = CSV_COLUMNS.filter((column) => rows.some((row) => row[column] != null));
  const lines = [columns.join(","), ...rows.map((row) => columns.map((column) => escapeCsv(row[column])).join(","))];
  const url = window.URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  window.URL.revokeObjectURL(url);
};

const ReportToast: React.FC<ReportToastOptions> = ({ title, counts, rows, successStatus, fileName }) => {
  const issues = rows.filter((row) => row.status !== successStatus);

  return (
    <div className="text-sm">
      <p className="font-semibold">
        {title}:{" "}
        {Object.entries(counts)
          .map(([name, count]) => `${count} ${name}`)
          .join(", ")}
      </p>
      {issues.length > 0 && (
        <ul className="mt-2 space-y-1 list-disc pl-4">
          {issues.slice(0, MAX_DISPLAYED_ROWS).map((row, index) => (
            <li key={index}>
              {row.row != null ? `Row ${row.row}` : `Label ${row.labelId}`}
              {row.row != null && row.labelId != null && ` (label ${row.labelId})`} – {row.status}
              {row.reason && `: ${row.reason}`}
            </li>
          ))}
        </ul>
      )}
      {issues.length > MAX_DISPLAYED_ROWS && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-gray-500">and {issues.length - MAX_DISPLAYED_ROWS} more ...</span>
          <button
            type="button"
            className="px-2 py-1 rounded border border-primary text-primary hover:bg-gray-100"
            onClick={(e) => {
              e.stopPropagation();
              downloadReport(rows, fileName);
            }}
          >
            Download report
          </button>
        </div>
      )}
    </div>
  );
};

export const showReportToast = (options: ReportToastOptions) => {
  const content = <ReportToast {...options} />;
  const issues = options.rows.filter((row) => row.status !== options.successStatus);
  if (issues.length === 0) {
    toast.success(content);
    return;
  }
  const notify = issues.some((row) => row.status === "rejected") ? toast.warning : toast.info;
  // stays open so the user can read the details or download the report
  notify(content, { autoClose: false, closeOnClick: false });
};
